/**
 * Frontier Editor — admin CRUD for all 40 frontier catalogues.
 *
 * Layout: catalogue sidebar (left) → entries table (middle) → form (right).
 * Form fields auto-render based on the schema inferred from existing entries.
 * Writes to the DB overlay; static data in source files remains untouched.
 *
 * Admin-only on the backend; non-admin users get a 403 banner here.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Pencil, Trash2, Plus, Search, Save, X, RefreshCw, Database,
  AlertCircle, Check, Zap, Download, Upload, History,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface FieldSpec {
  name: string
  type: 'string' | 'number' | 'bool' | 'list' | 'object' | 'enum' | 'lab-autocomplete'
  options?: string[]
}
interface CatalogueMeta { name: string; label: string; count: number; fields: FieldSpec[] }
interface Entry { [k: string]: unknown }
interface Ingestor { name: string; label: string; catalogue: string; description: string }
interface IngestorRunRow {
  id: number; catalogue: string; proposed: number; inserted: number; failed: number
  elapsed_ms: number; error: string | null; triggered_by: string; started_at: string | null
}

export default function FrontierEditorPage() {
  const [catalogues, setCatalogues] = useState<CatalogueMeta[]>([])
  const [activeCat, setActiveCat] = useState<string>('')
  const [entries, setEntries] = useState<Entry[]>([])
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Entry | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)
  const [ingestors, setIngestors] = useState<Ingestor[]>([])
  const [runningIngestor, setRunningIngestor] = useState<string | null>(null)
  const [labSuggestions, setLabSuggestions] = useState<string[]>([])
  const [history, setHistory] = useState<IngestorRunRow[]>([])
  const [historyOpen, setHistoryOpen] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [activeSchema, setActiveSchema] = useState<FieldSpec[] | null>(null)

  const showToast = (kind: 'ok' | 'err', msg: string) => {
    setToast({ kind, msg })
    setTimeout(() => setToast(null), 3500)
  }

  // Initial catalogue list + ingestors
  useEffect(() => {
    api.get('/api/frontier/editor/catalogues')
      .then(r => {
        setCatalogues(r.data?.catalogues || [])
        if (r.data?.catalogues?.length && !activeCat) {
          setActiveCat(r.data.catalogues[0].name)
        }
      })
      .catch(e => showToast('err', `Catalogue list failed: ${e.response?.status || 'network'}`))
    api.get('/api/frontier/editor/ingestors')
      .then(r => setIngestors(r.data?.ingestors || []))
      .catch(() => {})
    api.get('/api/frontier/editor/lab-suggestions')
      .then(r => setLabSuggestions(r.data?.labs || []))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ingestorsForActiveCat = useMemo(
    () => ingestors.filter(i => i.catalogue === activeCat),
    [ingestors, activeCat],
  )

  const runIngestor = async (name: string) => {
    setRunningIngestor(name)
    try {
      const r = await api.post(`/api/frontier/editor/ingestors/${name}/run`)
      const d = r.data
      showToast('ok', `${name}: +${d.inserted} entries (${d.elapsed_ms}ms)`)
      // Reload current catalogue
      if (activeCat) {
        const list = await api.get(`/api/frontier/editor/catalogues/${activeCat}`)
        setEntries(list.data?.items || [])
      }
    } catch (e: any) {
      showToast('err', e.response?.data?.detail || 'Ingestor run failed (admin only)')
    } finally {
      setRunningIngestor(null)
    }
  }

  // Entries + schema for current catalogue
  useEffect(() => {
    if (!activeCat) return
    setLoading(true)
    Promise.all([
      api.get(`/api/frontier/editor/catalogues/${activeCat}`)
        .then(r => setEntries(r.data?.items || [])).catch(() => setEntries([])),
      api.get(`/api/frontier/editor/catalogues/${activeCat}/schema`)
        .then(r => setActiveSchema(r.data?.fields || null))
        .catch(() => setActiveSchema(null)),
    ]).finally(() => setLoading(false))
    setEditing(null)
    setIsNew(false)
    setHistoryOpen(null)
  }, [activeCat])

  const activeMeta = catalogues.find(c => c.name === activeCat)

  const filtered = useMemo(() => {
    if (!search) return entries
    const q = search.toLowerCase()
    return entries.filter(e =>
      Object.values(e).some(v =>
        v != null && String(v).toLowerCase().includes(q)
      )
    )
  }, [entries, search])

  const titleFieldOf = (entry: Entry): string => {
    for (const k of ['title', 'name', 'model', 'agent', 'company', 'subject', 'witness',
                     'deal', 'platform', 'chip', 'org', 'plaintiff', 'publisher', 'lab',
                     'moment', 'jurisdiction']) {
      if (entry[k]) return String(entry[k])
    }
    return String(entry.id || '(untitled)')
  }

  const startEdit = (entry: Entry) => {
    setEditing({ ...entry })
    setIsNew(false)
  }

  const fields = activeSchema || activeMeta?.fields || []

  const startNew = () => {
    if (!fields.length) return
    const blank: Entry = { id: '' }
    for (const f of fields) {
      if (f.name === 'id') continue
      blank[f.name] = f.type === 'number' ? 0
                    : f.type === 'bool'   ? false
                    : f.type === 'list'   ? []
                    : f.type === 'object' ? {}
                    : ''
    }
    setEditing(blank)
    setIsNew(true)
  }

  const loadHistory = async (ingestorName: string) => {
    if (historyOpen === ingestorName) {
      setHistoryOpen(null)
      return
    }
    try {
      const r = await api.get(`/api/frontier/editor/ingestors/${ingestorName}/history`,
        { params: { limit: 10 } })
      setHistory(r.data?.runs || [])
      setHistoryOpen(ingestorName)
    } catch { /* ignore */ }
  }

  const exportEntries = (format: 'jsonl' | 'csv') => {
    if (!activeCat) return
    const url = `/api/frontier/editor/catalogues/${activeCat}/export?format=${format}`
    window.open(url, '_blank')
  }

  const importFile = async (file: File) => {
    if (!activeCat) return
    setImporting(true)
    try {
      const content = await file.text()
      const fmt = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'jsonl'
      const r = await api.post(
        `/api/frontier/editor/catalogues/${activeCat}/import`,
        { format: fmt, content })
      const d = r.data
      showToast('ok', `Imported: +${d.inserted}, failed ${d.failed}`)
      const list = await api.get(`/api/frontier/editor/catalogues/${activeCat}`)
      setEntries(list.data?.items || [])
    } catch (e: any) {
      showToast('err', e.response?.data?.detail || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  const save = async () => {
    if (!editing || !activeCat) return
    const entryId = (editing.id || editing.entry_id || '') as string
    if (!entryId) {
      showToast('err', 'id field is required')
      return
    }
    setSaving(true)
    try {
      await api.post(`/api/frontier/editor/catalogues/${activeCat}/entries`, {
        entry_id: entryId,
        data:     editing,
      })
      // Refetch list
      const r = await api.get(`/api/frontier/editor/catalogues/${activeCat}`)
      setEntries(r.data?.items || [])
      showToast('ok', isNew ? 'Created' : 'Saved')
      setEditing(null)
      setIsNew(false)
    } catch (e: any) {
      showToast('err', e.response?.data?.detail || 'Save failed (admin role required?)')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (entry: Entry) => {
    if (!activeCat) return
    const entryId = String(entry.id || '')
    if (!entryId) return
    if (!confirm(`Delete ${titleFieldOf(entry)}?`)) return
    try {
      await api.delete(`/api/frontier/editor/catalogues/${activeCat}/entries/${entryId}`)
      setEntries(entries.filter(e => e.id !== entry.id))
      showToast('ok', 'Deleted')
    } catch (e: any) {
      showToast('err', e.response?.data?.detail || 'Delete failed (admin role required?)')
    }
  }

  return (
    <div className="space-y-3 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Database size={18} className="text-emerald" /> FRONTIER EDITOR
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          admin CRUD · {catalogues.length} catalogues · {ingestors.length} ingestors · DB-served
        </span>
        {toast && (
          <span className={clsx(
            'badge text-[10px] flex items-center gap-1',
            toast.kind === 'ok' ? 'bg-emerald/15 text-emerald border border-emerald/30'
                                : 'bg-rose/15 text-rose-light border border-rose/30',
          )}>
            {toast.kind === 'ok' ? <Check size={10} /> : <AlertCircle size={10} />}
            {toast.msg}
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-3 min-h-[640px]">
        {/* Left: catalogue sidebar */}
        <aside className="col-span-3 card p-2 max-h-[80vh] overflow-y-auto">
          <div className="text-[9px] uppercase tracking-widest text-text-muted px-2 py-1.5">
            Catalogues ({catalogues.length})
          </div>
          <ul className="space-y-0.5">
            {catalogues.map(c => (
              <li key={c.name}>
                <button type="button" onClick={() => setActiveCat(c.name)}
                  className={clsx(
                    'w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between transition-colors',
                    activeCat === c.name
                      ? 'bg-purple/15 text-text-primary border border-purple/40'
                      : 'text-text-secondary hover:bg-surface-raised',
                  )}>
                  <span className="truncate">{c.label}</span>
                  <span className="text-[10px] text-text-muted font-mono shrink-0">{c.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Middle: entries list */}
        <section className={clsx('card p-3', editing ? 'col-span-5' : 'col-span-9')}>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-text-primary flex-1">
              {activeMeta?.label || activeCat || 'Select a catalogue'}
              <span className="text-[10px] text-text-muted ml-2 font-mono">
                {entries.length} entries
              </span>
            </h2>
            {ingestorsForActiveCat.map(ing => (
              <span key={ing.name} className="inline-flex items-center gap-0.5">
                <button type="button" onClick={() => runIngestor(ing.name)}
                  disabled={runningIngestor === ing.name}
                  title={ing.description}
                  className="btn-secondary text-[10px] inline-flex items-center gap-1">
                  {runningIngestor === ing.name
                    ? <RefreshCw size={10} className="animate-spin" />
                    : <Zap size={10} className="text-amber-light" />}
                  {ing.label}
                </button>
                <button type="button" onClick={() => loadHistory(ing.name)}
                  className="p-1 rounded hover:bg-surface-raised text-text-muted"
                  aria-label={`History for ${ing.label}`}
                  title="Run history">
                  <History size={11} />
                </button>
              </span>
            ))}
            <label className="btn-ghost text-[10px] inline-flex items-center gap-1 cursor-pointer"
              title="Bulk import JSONL or CSV">
              <Upload size={10} />
              {importing ? 'Importing…' : 'Import'}
              <input type="file" accept=".jsonl,.csv,.json" className="hidden"
                onChange={e => e.target.files?.[0] && importFile(e.target.files[0])}
                disabled={importing} />
            </label>
            <button type="button" onClick={() => exportEntries('jsonl')}
              className="btn-ghost text-[10px] inline-flex items-center gap-1"
              title="Download as JSONL">
              <Download size={10} /> JSONL
            </button>
            <button type="button" onClick={() => exportEntries('csv')}
              className="btn-ghost text-[10px] inline-flex items-center gap-1"
              title="Download as CSV">
              <Download size={10} /> CSV
            </button>
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
              <input className="input pl-7 text-xs w-[200px]"
                placeholder="Search entries"
                value={search} onChange={e => setSearch(e.target.value)}
                aria-label="Search entries" />
            </div>
            <button type="button" onClick={startNew} disabled={!activeMeta}
              className="btn-primary text-xs inline-flex items-center gap-1">
              <Plus size={12} /> New
            </button>
            <button type="button" onClick={() => activeCat && setActiveCat(activeCat)}
              disabled={loading} className="btn-ghost"
              aria-label="Refresh entries">
              <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {historyOpen && (
            <div className="card p-2 mb-2 bg-surface-raised">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">
                  History · {historyOpen}
                </span>
                <button type="button" onClick={() => setHistoryOpen(null)}
                  className="text-text-muted hover:text-text-primary"
                  aria-label="Close history">
                  <X size={11} />
                </button>
              </div>
              <table className="w-full text-[10px] font-mono">
                <thead>
                  <tr className="text-text-muted border-b border-border">
                    <th className="text-left py-1">Started</th>
                    <th className="text-left py-1">Trigger</th>
                    <th className="text-right py-1">Proposed</th>
                    <th className="text-right py-1">Inserted</th>
                    <th className="text-right py-1">Failed</th>
                    <th className="text-right py-1">ms</th>
                    <th className="text-left py-1">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(r => (
                    <tr key={r.id} className="border-b border-border/40">
                      <td className="py-0.5 text-text-secondary">{r.started_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-0.5 text-purple-light">{r.triggered_by.split(':')[0]}</td>
                      <td className="py-0.5 text-right">{r.proposed}</td>
                      <td className="py-0.5 text-right text-emerald">{r.inserted}</td>
                      <td className="py-0.5 text-right text-rose-light">{r.failed}</td>
                      <td className="py-0.5 text-right">{r.elapsed_ms}</td>
                      <td className="py-0.5 text-rose-light truncate">{r.error || ''}</td>
                    </tr>
                  ))}
                  {history.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-text-muted italic py-2">No runs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <ul className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {filtered.map((e, i) => (
              <li key={(e.id as string) || i}
                className={clsx('group flex items-center gap-2 p-2 rounded text-xs transition-colors',
                  editing?.id === e.id
                    ? 'bg-purple/10 border border-purple/40'
                    : 'hover:bg-surface-raised',
                )}>
                <span className="font-mono text-[10px] text-text-muted shrink-0 w-32 truncate">
                  {String(e.id || '—')}
                </span>
                <InlineTitle entry={e} title={titleFieldOf(e)}
                  onCommit={async (newTitle) => {
                    if (!activeCat) return
                    // Pick the field to commit to — first matching title-ish key
                    const titleKey = ['title', 'name', 'model', 'agent', 'company',
                                      'subject', 'witness', 'deal', 'platform', 'chip',
                                      'org', 'plaintiff', 'publisher', 'lab', 'moment',
                                      'jurisdiction'].find(k => e[k] !== undefined)
                    if (!titleKey) return
                    const updated = { ...e, [titleKey]: newTitle }
                    try {
                      await api.post(`/api/frontier/editor/catalogues/${activeCat}/entries`, {
                        entry_id: String(e.id || ''),
                        data:     updated,
                      })
                      setEntries(entries.map(x => x.id === e.id ? updated : x))
                      showToast('ok', 'Saved inline')
                    } catch (err: any) {
                      showToast('err', err.response?.data?.detail || 'Inline save failed')
                    }
                  }} />
                <button type="button" onClick={() => startEdit(e)}
                  className="p-1 rounded hover:bg-purple/20 text-purple-light"
                  aria-label="Edit full entry">
                  <Pencil size={11} />
                </button>
                <button type="button" onClick={() => remove(e)}
                  className="p-1 rounded hover:bg-rose/20 text-rose-light"
                  aria-label="Delete">
                  <Trash2 size={11} />
                </button>
              </li>
            ))}
            {filtered.length === 0 && !loading && (
              <li className="text-center text-text-muted text-xs italic py-8">
                {entries.length === 0 ? 'No entries.' : 'No matches for current search.'}
              </li>
            )}
          </ul>
        </section>

        {/* Right: edit panel */}
        {editing && fields.length > 0 && (
          <section className="col-span-4 card p-3 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-text-primary flex-1">
                {isNew ? 'New entry' : `Edit · ${titleFieldOf(editing)}`}
              </h2>
              <button type="button" onClick={save} disabled={saving}
                className="btn-primary text-xs inline-flex items-center gap-1">
                <Save size={11} /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={() => { setEditing(null); setIsNew(false) }}
                className="btn-ghost"
                aria-label="Close edit panel">
                <X size={12} />
              </button>
            </div>

            <div className="space-y-2">
              {fields.map(f => (
                <FieldEditor key={f.name} field={f}
                  value={editing[f.name]}
                  labSuggestions={labSuggestions}
                  onChange={v => setEditing({ ...editing, [f.name]: v })} />
              ))}
            </div>
          </section>
        )}
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Static catalogues live in source; edits persist in a DB overlay. Tombstones
        hide static entries; new entries (DB-only) appear after the static set. Admin role required for writes.
      </p>
    </div>
  )
}

function InlineTitle({ entry, title, onCommit }: {
  entry: Entry
  title: string
  onCommit: (newTitle: string) => Promise<void> | void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ = entry  // keep entry in closure if we need it later

  if (!editing) {
    return (
      <button type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); setDraft(title) }}
        className="flex-1 text-left text-text-primary truncate hover:text-blue-light hover:bg-surface-raised/50 rounded px-1 -mx-1"
        title="Click to inline-edit title">
        {title}
      </button>
    )
  }

  const commit = async () => {
    if (draft && draft !== title) await onCommit(draft)
    setEditing(false)
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-autofocus
    <input className="input text-xs flex-1" autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit() }
        else if (e.key === 'Escape') { setEditing(false); setDraft(title) }
      }}
      onClick={e => e.stopPropagation()}
      aria-label="Inline title editor" />
  )
}


function FieldEditor({ field, value, onChange, labSuggestions = [] }: {
  field: FieldSpec
  value: unknown
  onChange: (v: unknown) => void
  labSuggestions?: string[]
}) {
  const label = (
    <div className="flex items-baseline justify-between mb-0.5">
      <label className="text-[10px] uppercase tracking-widest text-text-muted">
        {field.name}
      </label>
      <span className="text-[9px] text-text-muted font-mono">{field.type}</span>
    </div>
  )

  if (field.type === 'enum') {
    return (
      <div>
        {label}
        <select className="input text-xs w-full"
          aria-label={field.name}
          value={(value as string | undefined) ?? ''}
          onChange={e => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    )
  }
  if (field.type === 'lab-autocomplete') {
    return (
      <div>
        {label}
        <input className="input text-xs w-full" list={`labs-${field.name}`}
          value={(value as string | undefined) ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Anthropic, OpenAI, …" />
        <datalist id={`labs-${field.name}`}>
          {labSuggestions.map(l => <option key={l} value={l} />)}
        </datalist>
      </div>
    )
  }

  if (field.type === 'bool') {
    return (
      <div>
        {label}
        <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
          <input type="checkbox" checked={Boolean(value)}
            onChange={e => onChange(e.target.checked)} />
          {Boolean(value) ? 'true' : 'false'}
        </label>
      </div>
    )
  }
  if (field.type === 'number') {
    return (
      <div>
        {label}
        <input className="input text-xs w-full" type="number"
          aria-label={field.name} title={field.name}
          value={(value as number | undefined) ?? ''}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} />
      </div>
    )
  }
  if (field.type === 'list') {
    const arr = Array.isArray(value) ? value : []
    return (
      <div>
        {label}
        <textarea className="input text-xs w-full h-20 font-mono"
          aria-label={field.name} title={field.name}
          value={arr.join('\n')}
          onChange={e => onChange(e.target.value.split('\n').filter(Boolean))}
          placeholder="One item per line" />
      </div>
    )
  }
  if (field.type === 'object') {
    return (
      <div>
        {label}
        <textarea className="input text-xs w-full h-24 font-mono"
          aria-label={field.name} title={field.name}
          placeholder="{}"
          value={value ? JSON.stringify(value, null, 2) : '{}'}
          onChange={e => {
            try { onChange(JSON.parse(e.target.value)) }
            catch { /* keep typing */ }
          }} />
      </div>
    )
  }
  // string
  const isLong = field.name === 'summary' || field.name === 'description' || field.name === 'highlight'
  return (
    <div>
      {label}
      {isLong ? (
        <textarea className="input text-xs w-full h-24"
          aria-label={field.name} title={field.name} placeholder={field.name}
          value={(value as string | undefined) ?? ''}
          onChange={e => onChange(e.target.value)} />
      ) : (
        <input className="input text-xs w-full"
          aria-label={field.name} title={field.name} placeholder={field.name}
          value={(value as string | undefined) ?? ''}
          onChange={e => onChange(e.target.value)} />
      )}
    </div>
  )
}
